import appLanding from "../data/appLanding";

const SCHEMA_CATEGORY = {
  cat_game: "GameApplication",
  cat_party: "GameApplication",
  cat_trivia: "GameApplication",
  cat_casino: "GameApplication",
  cat_education: "EducationalApplication",
  cat_health: "HealthApplication",
  cat_utility: "UtilitiesApplication",
};

function uniquePhrases(phrases) {
  const seen = new Set();
  const out = [];
  for (const raw of phrases) {
    const phrase = String(raw || "")
      .replace(/\s+/g, " ")
      .trim();
    if (!phrase) continue;
    const key = phrase.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(phrase);
  }
  return out;
}

export function deriveKeywords(app) {
  const title = app.title || "";
  const shortTitle = app.shortTitle || "";
  const phrases = [title, shortTitle];
  if (title && !/\bapp\b/i.test(title)) phrases.push(`${title} App`);
  if (shortTitle && !/\bapp\b/i.test(shortTitle)) {
    phrases.push(`${shortTitle} App`);
  }
  if (title.includes(":")) {
    const right = title.split(":").slice(1).join(":").trim();
    phrases.push(right);
    if (right && !/\bapp\b/i.test(right)) phrases.push(`${right} App`);
  }
  return uniquePhrases(phrases).slice(0, 12);
}

function defaultFaq(app, landing) {
  const name = app.shortTitle || app.title;
  const primary = (landing.keywords && landing.keywords[0]) || app.title;
  const stores = [
    app.appStoreUrl ? "the App Store" : null,
    app.playStoreUrl ? "Google Play" : null,
  ].filter(Boolean);
  const storeText =
    stores.length > 0 ? stores.join(" and ") : "the App Store and Google Play";

  return [
    {
      q: `What is ${name}?`,
      a:
        landing.seoDescription ||
        `${app.title} is a MindBlows app for iPhone and Android.`,
    },
    {
      q: `Where can I download the ${primary}?`,
      a: `${name} is free to download on ${storeText}.`,
    },
    {
      q: `Is ${name} free?`,
      a: `Yes. ${name} is free to download. Optional Pro upgrades are available in the app.`,
    },
  ];
}

export function resolveLanding(app) {
  if (!app) return null;
  const extras = appLanding[app.id] || {};
  const keywords = uniquePhrases([
    ...(Array.isArray(app.keywords) ? app.keywords : []),
    ...(Array.isArray(extras.keywords) ? extras.keywords : []),
    ...deriveKeywords(app),
  ]).slice(0, 12);

  const seoTitle = app.seoTitle || extras.seoTitle || app.title;
  const seoDescription =
    app.seoDescription || extras.seoDescription || app.subtitle || app.title;
  const description = app.description || extras.description || "";
  const features = (
    Array.isArray(app.features) && app.features.length
      ? app.features
      : extras.features || []
  ).filter(Boolean);
  const faq =
    Array.isArray(app.faq) && app.faq.length
      ? app.faq
      : extras.faq && extras.faq.length
        ? extras.faq
        : defaultFaq(app, { seoDescription, keywords });

  return {
    seoTitle,
    seoDescription,
    keywords,
    description,
    features,
    faq,
  };
}

export function softwareJsonLd(app, landing, pageUrl) {
  const os = [
    app.appStoreUrl ? "iOS" : null,
    app.playStoreUrl ? "Android" : null,
  ]
    .filter(Boolean)
    .join(", ");

  const json = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: app.shortTitle || app.title,
    alternateName: uniquePhrases([
      app.title,
      landing.seoTitle,
      ...(landing.keywords || []),
    ]).filter((name) => name !== (app.shortTitle || app.title)),
    description: landing.seoDescription,
    url: pageUrl,
    image: app.icon,
    applicationCategory: SCHEMA_CATEGORY[app.categoryKey] || "MobileApplication",
    operatingSystem: os || "iOS, Android",
    author: {
      "@type": "Organization",
      name: "MindBlows",
      url: "https://mindblows.com",
    },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  };

  if (app.appStoreUrl) json.downloadUrl = app.appStoreUrl;
  if (app.playStoreUrl) {
    json.installUrl = app.playStoreUrl;
  }
  return json;
}

export function faqJsonLd(faq) {
  if (!Array.isArray(faq) || faq.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
}
